'use client';

import { useState, type CSSProperties } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { SIcon } from '../SIcon';
import { SGhostButton } from '../SGhostButton';
import { sanitizeInlineHtml } from '../../lib/sanitize-inline-html';
import { usePortalContainer } from '../../lib/portal-container';
import { cn } from '../../lib/cn';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';

export type SGuideType = 'tip' | 'notion';

/** 중첩 배열로 depth 표현 (원본 renderListItem 대응). 예: ['상위', ['하위1', '하위2']] */
export type SGuideMessage = string | SGuideMessage[];

const DEFAULT_LABEL: Record<SGuideType, string> = { tip: '활용 TIP', notion: '사용법 안내' };
const ICON: Record<SGuideType, 'helpOutline' | 'notion'> = { tip: 'helpOutline', notion: 'notion' };

/** 중첩 메시지를 depth가 매겨진 평면 목록으로 변환 (원본 renderListItem: 배열이면 depth+1) */
function flattenMessages(message: SGuideMessage, depth = 0): { text: string; depth: number }[] {
  if (Array.isArray(message)) return message.flatMap(m => flattenMessages(m, depth + 1));
  return message !== '' ? [{ text: message, depth }] : [];
}

export interface SGuideProps {
  /** 가이드 타입 (tip: 팝업, notion: 외부 링크) */
  type?: SGuideType;
  /** 버튼 레이블 (없으면 type 기본값) */
  label?: string;
  /** 팝업 메시지 (배열이면 리스트, 중첩 배열이면 depth 표현) */
  message?: SGuideMessage;
  /** notion 타입 클릭 시 이동 URL */
  url?: string;
  /** 팝업 제목 (없으면 type 기본값) */
  popupTitle?: string;
  /** 팝업 너비 (px, 기본 426) */
  popupWidth?: number;
  className?: string;
  style?: CSSProperties;
}

/** SGuide — sd-guide 포팅. tip=팝업 안내, notion=외부 링크. */
export function SGuide({
  type = 'tip',
  label = '',
  message = '',
  url = '',
  popupTitle = '',
  popupWidth,
  className,
  style,
}: SGuideProps) {
  const [open, setOpen] = useState(false);
  const portalContainer = usePortalContainer();
  const isNotion = type === 'notion';
  const defaultLabel = DEFAULT_LABEL[type];
  const iconName = ICON[type];
  const messages = flattenMessages(message);

  // 원본 커스텀 pill 버튼 (표준 SButton 아님): active(팝업 열림) 시 tip=초록/notion=파랑 배경
  const active = open;
  const activeBg =
    type === 'tip' ? 'var(--cmp-guide-button-bg-tip)' : 'var(--cmp-guide-button-bg-notion)';
  const iconColor = active
    ? 'var(--cmp-guide-button-icon-active)'
    : type === 'tip'
      ? 'var(--cmp-guide-button-icon-default)'
      : 'var(--sys-color-fg-primary)';

  const trigger = (
    <button
      type="button"
      onClick={() => {
        if (isNotion) {
          if (url !== '') window.open(url, '_blank', 'noopener,noreferrer');
          return;
        }
        setOpen(o => !o);
      }}
      className="inline-flex h-[var(--cmp-guide-button-height)] cursor-pointer items-center gap-[var(--cmp-guide-button-gap)] whitespace-nowrap rounded-[var(--cmp-guide-button-radius)] border-[length:var(--cmp-guide-button-border-width)] border-solid px-[var(--cmp-guide-button-paddingX)] text-[12px] font-medium leading-[20px] transition-colors"
      style={{
        borderColor: active ? activeBg : 'var(--cmp-guide-button-border-default)',
        background: active ? activeBg : 'var(--cmp-guide-button-bg-default)',
        color: active
          ? 'var(--cmp-guide-button-text-active)'
          : 'var(--cmp-guide-button-text-default)',
      }}
    >
      <SIcon name={iconName} size={16} color={iconColor} />
      {label || defaultLabel}
    </button>
  );

  // notion 타입은 팝업 없이 링크만
  if (isNotion)
    return (
      <span className={className} style={style}>
        {trigger}
      </span>
    );

  return (
    <RPopover.Root open={open} onOpenChange={setOpen}>
      <RPopover.Trigger asChild>{trigger}</RPopover.Trigger>
      <RPopover.Portal container={portalContainer}>
        <RPopover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className={cn(
            'relative z-50 rounded-[var(--cmp-guide-contents-radius)] bg-[var(--color-white)]',
            FLOATING_SLIDE_ANIM,
          )}
          style={{
            width: popupWidth != null ? popupWidth : 426,
            padding: 'var(--cmp-guide-contents-paddingY) var(--cmp-guide-contents-paddingX)',
            boxShadow: '4px 4px 24px 4px rgba(0,0,0,0.1)',
          }}
        >
          <div className="absolute right-[12px] top-[12px]">
            <SGhostButton icon="close" size="sm" ariaLabel="close" onClick={() => setOpen(false)} />
          </div>
          <div className="flex items-center gap-[var(--cmp-guide-contents-title-gap)]">
            <SIcon
              name={iconName}
              size={24}
              color={isNotion ? 'var(--sys-color-fg-primary)' : 'var(--cmp-guide-contents-icon)'}
            />
            <h3 className="m-0 text-[16px] font-bold leading-[26px] text-[color:var(--cmp-guide-contents-typography-color)]">
              {popupTitle || defaultLabel}
            </h3>
          </div>
          {/* 원본 sd-guide: '-' 커스텀 불릿(::before) + <p> innerHTML. depth-2 는 '•' + 들여쓰기 */}
          <ul className="m-0 mt-[var(--cmp-guide-contents-gap)] flex w-full list-none flex-col gap-[var(--cmp-guide-contents-body-gap)] p-0 text-[12px] leading-[20px] text-[color:var(--cmp-guide-contents-typography-color)]">
            {messages.map((m, i) => (
              <li
                key={i}
                className={cn(
                  'flex w-full items-start before:ml-[10px] before:mr-[12px] before:block before:w-[6px] before:flex-shrink-0',
                  m.depth >= 2
                    ? "pl-(--cmp-guide-contents-depth-paddingLeft) before:content-['•']"
                    : "before:content-['-']",
                )}
              >
                <p
                  className="m-0 w-full min-w-0 break-words"
                  dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(m.text) }}
                />
              </li>
            ))}
          </ul>
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}
